import PropTypes from 'prop-types';
import { forwardRef, useEffect, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

// material-ui
import { useTheme } from '@mui/material/styles';
import { Avatar, Chip, ListItemButton, ListItemIcon, ListItemText, Typography, useMediaQuery } from '@mui/material';

// project imports
import { MENU_OPEN, SET_MENU } from 'store/actions';
import config from 'config';

// assets
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import LoginContext from 'Context/ContextLogin';

// ==============================|| SIDEBAR MENU LIST ITEMS ||============================== //

const NavItem = ({ item, level }) => {
    const theme = useTheme();
    const context = useContext(LoginContext);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const customization = useSelector((state) => state.customization);
    const matchesSM = useMediaQuery(theme.breakpoints.down('lg'));

    const Icon = item.icon;
    const itemIcon = item?.icon ? (
        <Icon stroke={1.5} style={{ width: '1.3rem' }} />
    ) : (
        <FiberManualRecordIcon
            sx={{
                width: customization.isOpen.findIndex((id) => id === item?.id) > -1 ? 8 : 6,
                height: customization.isOpen.findIndex((id) => id === item?.id) > -1 ? 8 : 6
            }}
            fontSize={level > 0 ? 'inherit' : 'medium'}
        />
    );

    let itemTarget = '_self';
    if (item.target) {
        itemTarget = '_blank';
    }

    // concatenacion de url
    const buildUrl = () => {
        // Si basename es '/' y url es '/', retornar solo '/'
        if (config.basename === '/' && item.url === '/') {
            return '/';
        }
        // Si basename termina en '/' y url empieza con '/', quitar una barra
        if (config.basename.endsWith('/') && item.url.startsWith('/')) {
            return config.basename.slice(0, -1) + item.url;
        }
        // Si basename NO termina en '/' y url NO empieza con '/', agregar una barra
        if (!config.basename.endsWith('/') && !item.url.startsWith('/')) {
            return config.basename + '/' + item.url;
        }
        // Caso normal
        return config.basename + item.url;
    };

    const finalUrl = buildUrl();

    let listItemProps = {
        component: forwardRef((props, ref) => <Link ref={ref} {...props} to={finalUrl} target={itemTarget} />)
    };
    if (item?.external) {
        listItemProps = { component: 'a', href: item.url, target: itemTarget };
    }

    const itemHandler = (id) => {
        dispatch({ type: MENU_OPEN, id });
        if (matchesSM) dispatch({ type: SET_MENU, opened: false });
        if (window.location.pathname === finalUrl) {
            navigate(finalUrl, {
                state: { key: Date.now() },
                replace: false
            });
        } else {
            navigate(finalUrl);
        }
    };

    // active menu item on page load
    useEffect(() => {
        const currentIndex = document.location.pathname
            .toString()
            .split('/')
            .findIndex((id) => id === item.id);
        if (currentIndex > -1) {
            dispatch({ type: MENU_OPEN, id: item.id });
        }
        // eslint-disable-next-line
    }, []);
    return (
        <ListItemButton
            id={`btn${item?.id}SideBar`}
            {...listItemProps}
            disabled={item.disabled}
            sx={{
                borderRadius: `${customization.borderRadius}px`,
                mb: context?.login?.usuario?.idColaborador ? 5 : 0.5,
                textAlign: context?.login?.usuario?.idColaborador ? 'center' : 'left',
                alignItems: 'flex-center',
                backgroundColor: level > 1 ? 'transparent !important' : '#b6a268',
                py: level > 1 ? 1 : 1.25,
                pl: `${level * 24}px`
            }}
            onClick={() => itemHandler(item.id)}
        >
            {!context?.login?.usuario?.idColaborador && (
                <ListItemIcon sx={{ my: 'auto', minWidth: !item?.icon ? 18 : 36, color: theme.palette.dark.main }}>{itemIcon}</ListItemIcon>
            )}
            <ListItemText
                primary={
                    <Typography variant="menuTitle" align={context?.login?.usuario?.idColaborador ? 'center' : ''} color="#FFFFFF">
                        {item.title}
                    </Typography>
                }
                secondary={
                    item.caption && (
                        <Typography variant="caption" sx={{ ...theme.typography.subMenuCaption }} display="block" gutterBottom>
                            {item.caption}
                        </Typography>
                    )
                }
            />
            {item.chip && (
                <Chip
                    color={item.chip.color}
                    variant={item.chip.variant}
                    size={item.chip.size}
                    label={item.chip.label}
                    avatar={item.chip.avatar && <Avatar>{item.chip.avatar}</Avatar>}
                />
            )}
        </ListItemButton>
    );
};

NavItem.propTypes = {
    item: PropTypes.object,
    level: PropTypes.number
};

export default NavItem;
